// Clouds — a slow-drifting translucent veil above the planet surface.
// The layer is intentionally sparse and faceted: more like thin mineral
// weather bands than fluffy photoreal clouds.

import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group, Mesh } from 'three';

import { sceneAtProgress } from './sceneState';
import { SCENES } from './planetSections';

const CLOUDS_RADIUS = 1.66;
const CLOUD_COLOR = new THREE.Color('#efe8dc');
const CLOUD_COOL = new THREE.Color('#b9dec9');
const CLOUD_WARM = new THREE.Color('#c7ad78');
const SPIN_SPEED = 0.012; // rad/s on Y, slightly slower than planet's autorotate
const CLOUD_BASE = new THREE.Color('#ffffff');
const CLOUD_TINT = new THREE.Color();
const CLOUD_NEXT = new THREE.Color();

// Deterministic value-noise based on integer seed + 3 floats. Used to
// pick which vertices become "cloud puffs" and how far they pop out.
// Hand-rolled because we have no external noise lib available and we
// only need pseudo-random but stable per-vertex output.
function hash3(x: number, y: number, z: number, seed: number): number {
  // sin-based hash, cheap and deterministic across reloads
  let h = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719 + seed * 0.173) * 43758.5453;
  return h - Math.floor(h);
}

// Smoothstep helper.
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

export function Clouds() {
  const groupRef = useRef<Group | null>(null);
  const meshRef = useRef<Mesh | null>(null);

  // Build the cloud geometry once. We start with a sphere, then displace
  // a sparse subset of vertices outward — that's what makes the
  // surface read as "scattered cloud puffs" instead of a smooth ball.
  const geometry = useMemo(() => {
    const geo = new THREE.SphereGeometry(CLOUDS_RADIUS, 36, 24);
    const positions = geo.attributes.position;
    const colors = new Float32Array(positions.count * 3);
    const tmp = new THREE.Color();

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      const z = positions.getZ(i);

      const len = Math.sqrt(x * x + y * y + z * z) || 1;
      const nx = x / len;
      const ny = y / len;
      const nz = z / len;

      const longitude = Math.atan2(nz, nx);
      const latitude = Math.asin(Math.max(-1, Math.min(1, ny)));
      const band =
        0.5 + 0.5 * Math.sin(longitude * 3.2 + latitude * 7.6 + hash3(nx * 3.1, ny * 3.1, nz * 3.1, 8) * 2.8);
      const scatter = hash3(nx * 5.8, ny * 5.8, nz * 5.8, 11);
      const vein = hash3(nx * 13.0 - 2.0, ny * 13.0 + 4.0, nz * 13.0, 19);
      const density = band * 0.58 + scatter * 0.28 + vein * 0.14;
      const puffAmount = smoothstep(0.58, 0.92, density);

      const newR = CLOUDS_RADIUS + 0.035 * puffAmount;
      positions.setXYZ(i, nx * newR, ny * newR, nz * newR);

      tmp.copy(CLOUD_COLOR)
        .lerp(CLOUD_COOL, smoothstep(0.50, 0.92, band) * 0.22)
        .lerp(CLOUD_WARM, smoothstep(0.72, 1.0, vein) * 0.10)
        .multiplyScalar(0.78 + 0.26 * puffAmount);
      colors[i * 3 + 0] = tmp.r;
      colors[i * 3 + 1] = tmp.g;
      colors[i * 3 + 2] = tmp.b;
    }

    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.computeVertexNormals();
    return geo;
  }, []);

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    const dt = Math.min(delta, 1 / 30);
    groupRef.current.rotation.y += dt * SPIN_SPEED;
    groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.05) * 0.045;

    // Soft cloud wash toward the chapter signal color.
    const p =
      typeof window !== 'undefined' && typeof window.__planetProgress === 'number'
        ? window.__planetProgress
        : 0;
    const { idx, blendIn } = sceneAtProgress(p);
    const morph = window.__planetMorph ?? 0;
    const scene = SCENES[idx] ?? SCENES[0];
    const next = SCENES[Math.min(idx + 1, SCENES.length - 1)];
    const edge = blendIn > 0.72 ? (blendIn - 0.72) / 0.28 : 0;
    CLOUD_TINT.set(scene.signalColor);
    if (edge > 0 && idx < SCENES.length - 1) {
      CLOUD_TINT.lerp(CLOUD_NEXT.set(next.signalColor), edge);
    }
    CLOUD_BASE.setRGB(1, 1, 1).lerp(CLOUD_TINT, 0.28);
    const mat = meshRef.current?.material as THREE.MeshStandardMaterial | undefined;
    if (mat) {
      mat.color.lerp(CLOUD_BASE, 0.08);
      mat.opacity = (0.2 + (scene.atmosphereOpacity ?? 0.12) * 0.35) * (1 - morph * 0.96);
    }
  });

  return (
    <group ref={groupRef} name="clouds" raycast={() => null}>
      <mesh
        ref={meshRef}
        name="clouds-mesh"
        geometry={geometry}
        raycast={() => null}
      >
        <meshStandardMaterial
          vertexColors
          flatShading
          transparent
          opacity={0.24}
          roughness={1}
          metalness={0}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

export default Clouds;
